import React, {useMemo} from 'react';
import {Select} from 'antd';
import {useQuery} from '@apollo/client';
import {
    GET_PICKUP_POINT_OPTIONS,
    PickupPointOption,
    PickupPointOptionsData
} from '../graphql/queries/getPickupPointOptions';
import {PickupTaskEndPointType, PickupTaskStartPointType} from '../types/PickupTaskType';

const {Option, OptGroup} = Select;

interface PointSelectorValue {
    type: number;
    value: string;
    name?: string;
}

interface PointSelectorProps {
    type: 'start' | 'end';
    warehouseId: string | null;
    taskId?: string;
    value?: PointSelectorValue | string; // 支持新旧两种格式
    onChange?: (value: string | null, option?: any) => void; // 统一使用复合字符串格式
    placeholder?: string;
    disabled?: boolean;
    allowClear?: boolean;
    showSearch?: boolean;
    customPointOptions?: PickupPointOptionsData; // 可选的自定义点位选项数据
}

const PointSelector: React.FC<PointSelectorProps> = ({
                                                         type,
                                                         warehouseId,
                                                         taskId,
                                                         value,
                                                         onChange,
                                                         placeholder,
                                                         disabled = false,
                                                         allowClear = true,
                                                         showSearch = true,
                                                         customPointOptions
                                                     }) => {
    // 兼容性处理：如果 value 是字符串（旧格式），转换为对象格式
    const normalizedValue = useMemo((): PointSelectorValue | undefined => {
        if (!value) return undefined;

        if (typeof value === 'string') {
            // 旧格式：解析 "type-value" 字符串
            const [typeStr, val] = value.split('-');
            return {
                type: parseInt(typeStr),
                value: val,
                name: ''
            };
        }

        // 对象格式，直接使用
        const objectValue = value as PointSelectorValue;
        // 确保 type 和 value 都存在且有效
        if (objectValue.type !== undefined && objectValue.value) {
            return objectValue;
        }
        return undefined;
    }, [value]);

    // 处理 onChange 回调
    const handleChange = (selectedValue: string | null, option: any) => {
        if (!onChange) return;

        if (!selectedValue) {
            onChange(null);
            return;
        }

        // 解析复合值
        const [pointTypeStr, pointValue] = selectedValue.split('-');
        const pointType = parseInt(pointTypeStr);

        // 从 option 中获取名称
        const pointName = option?.children || option?.['data-name'] || '';

        // 直接调用 onChange，传递复合字符串值
        onChange(selectedValue, option);
    };
    const {data, loading, error} = useQuery(GET_PICKUP_POINT_OPTIONS, {
        variables: {
            input: {
                warehouseId: warehouseId || '',
                taskId
            }
        },
        skip: !warehouseId || !!customPointOptions, // 如果有自定义数据，跳过查询
        fetchPolicy: 'network-only',
        onError: (error) => {
            console.error('PointSelector query error:', error);
        }
    });


    const renderOption = (point: PickupPointOption, pointType: number) => {
        const complexValue = `${pointType}-${point.value}`;
        return (
            <Option
                key={`${pointType}-${point.value}`} // 使用 pointType-value 作为key确保唯一性
                value={complexValue} // 使用复合value: type-value
                data-type={pointType}
                data-value={point.value}
                data-name={point.name}
                title={point.fullAddress || point.name}
            >
                {point.name}
            </Option>
        );
    };

    if (error) {
        console.error('Error loading pickup point options:', error);
        return (
            <Select
                placeholder="Error loading options"
                disabled
                value={value}
            />
        );
    }

    const pointOptions: PickupPointOptionsData = customPointOptions || data?.getPickupPointOptions || {
        hubList: [],
        rallyPointList: [],
        firstAppointmentAddress: undefined,
        warehouseList: []
    };

    // 将 value 对象转换为内部使用的字符串格式
    const getInternalValue = (): string | undefined => {
        if (!normalizedValue) return undefined;
        return `${normalizedValue.type}-${normalizedValue.value}`;
    };
    return (
        <Select
            value={getInternalValue()}
            onChange={handleChange}
            placeholder={placeholder}
            disabled={disabled || (loading && !customPointOptions)}
            allowClear={allowClear}
            showSearch={showSearch}
            optionFilterProp="children"
            loading={loading && !customPointOptions}
            dropdownStyle={{
                maxHeight: 400,
                overflow: 'auto'
            }}
            optionLabelProp="children"
        >
            {/* Hub 选项组 */}
            <OptGroup label="Hub">
                {pointOptions.hubList.map((hub, index) => {
                    const hubPointType = type === 'start' ? PickupTaskStartPointType.HUB : PickupTaskEndPointType.HUB;
                    return renderOption(hub, hubPointType);
                })}
            </OptGroup>

            {/* 仓库选项组 - 仅在起点选择时显示，且显示在 Hub 下面 */}
            {type === 'start' && pointOptions.warehouseList && pointOptions.warehouseList.length > 0 && (
                <OptGroup label="Warehouse">
                    {pointOptions.warehouseList.map((warehouse, index) => {
                        const warehousePointType = PickupTaskStartPointType.WAREHOUSE;
                        return renderOption(warehouse, warehousePointType);
                    })}
                </OptGroup>
            )}

            {/* 中继点选项组 - 仅在起点选择时显示 */}
            {type === 'start' && pointOptions.rallyPointList.length > 0 && (
                <OptGroup label="Rally Point">
                    {pointOptions.rallyPointList.map((rallyPoint, index) => {
                        const rallyPointType = PickupTaskStartPointType.RALLY_POINT;
                        return renderOption(rallyPoint, rallyPointType);
                    })}
                </OptGroup>
            )}

            {/* 第一个预约单选项组 - 仅在起点选择时显示 */}
            {type === 'start' && pointOptions.firstAppointmentAddress && (
                <OptGroup label="First Appointment">
                    {renderOption(pointOptions.firstAppointmentAddress, PickupTaskStartPointType.FIRST_APPOINTMENT)}
                </OptGroup>
            )}
        </Select>
    );
};

export default PointSelector;